(() => {
  const ONETIME_SELLING_PLAN_ID = 0;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    const t = thousands ?? ',';
    const d = decimal ?? '.';
    if (Number.isNaN(cents) || cents == null) return '0';
    const num = (Number(cents) / 100).toFixed(precision);
    const parts = num.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${t}`);
    const centsPart = parts[1] ? d + parts[1] : '';
    return dollars + centsPart;
  }

  function formatMoneyWithTemplate(cents, formatString) {
    const fmt = String(formatString);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholderRegex);
    if (!match) return null;
    const key = match[1];
    let value = '';
    switch (key) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(placeholderRegex, value);
  }

  function formatMoney(cents, format) {
    const n = Math.round(Number(cents));
    if (Number.isNaN(n)) return '';
    const fmt = format && String(format).trim();
    if (fmt && /\{\{\s*\w+\s*\}\}/.test(fmt)) {
      const templated = formatMoneyWithTemplate(n, fmt);
      if (templated != null) return templated;
    }
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function' && fmt) {
      return window.Shopify.formatMoney(n, fmt);
    }
    return (n / 100).toFixed(2);
  }

  function applyTemplate(template, vars) {
    return String(template || '').replace(/\[(\w+)\]/g, (_, key) => {
      const value = vars[key];
      return value == null ? '' : String(value);
    });
  }

  function parseConfig(root) {
    const el = root.querySelector('[data-clc-bundle-config]');
    if (!el || !el.textContent.trim()) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function sortTiers(tiers) {
    return [...(tiers || [])].sort((a, b) => Number(a.minCans) - Number(b.minCans));
  }

  function getRechargeCdnApi(recharge) {
    return recharge?.cdn || recharge;
  }

  function getRechargeBundleEngine(recharge) {
    return recharge?.bundle || recharge;
  }

  function getCdnBundleSettingsFn(recharge) {
    const cdn = getRechargeCdnApi(recharge);
    return (
      cdn?.getCDNBundleSettings ||
      cdn?.getCdnBundleSettings ||
      recharge?.getCDNBundleSettings ||
      recharge?.getCdnBundleSettings
    );
  }

  function waitForRecharge(timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
      const isReady = () => {
        const recharge = window.recharge;
        return recharge && (getCdnBundleSettingsFn(recharge) || getRechargeBundleEngine(recharge)?.getDynamicBundleItems);
      };

      if (isReady()) {
        resolve(window.recharge);
        return;
      }
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (isReady()) {
          window.clearInterval(timer);
          resolve(window.recharge);
        } else if (Date.now() - started > timeoutMs) {
          window.clearInterval(timer);
          const hasRecharge = Boolean(window.recharge);
          reject(
            new Error(
              hasRecharge
                ? 'Recharge SDK loaded but bundle API is unavailable. Check Recharge app embed and bundle product settings.'
                : 'Recharge SDK not loaded. Enable Recharge app embed or deploy the latest clc-bundle section.'
            )
          );
        }
      }, 100);
    });
  }

  function calculateDiscount(priceCents, discountValue, discountType) {
    const value = Number(discountValue) || 0;
    if (discountType === 'fixed_amount') {
      return Math.max(0, priceCents - Math.round(value * 100));
    }
    return Math.round(priceCents * (1 - value / 100));
  }

  function normalizeSellingPlanId(raw) {
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }

  function getEligibleTieredDiscounts(tieredDiscounts, chargeType, sellingPlanId) {
    const isOnetime = !sellingPlanId || Number(sellingPlanId) === ONETIME_SELLING_PLAN_ID;
    const lineItemType = isOnetime ? 'onetime' : 'subscription';

    return (tieredDiscounts || []).filter(
      ({ eligible_charge_types, eligible_line_item_types, status }) =>
        status === 'published' &&
        eligible_charge_types?.includes(chargeType) &&
        eligible_line_item_types?.includes(lineItemType)
    );
  }

  function getActiveTieredDiscount(tieredDiscounts, chargeType, sellingPlanId, maxBundleQuantity) {
    const eligible = getEligibleTieredDiscounts(tieredDiscounts, chargeType, sellingPlanId);
    if (!eligible.length) return null;

    const discount = eligible[0];
    return {
      ...discount,
      tiers: (discount.tiers || [])
        .map(({ tier }) => tier)
        .filter((tier) => Number(tier.condition_quantity_gte) <= maxBundleQuantity)
        .sort((a, b) => Number(a.condition_quantity_gte) - Number(b.condition_quantity_gte)),
    };
  }

  function getCurrentRechargeTier(activeTieredDiscount, totalSelected) {
    if (!activeTieredDiscount?.tiers?.length) return null;
    const qualifying = activeTieredDiscount.tiers.filter(
      (tier) => Number(tier.condition_quantity_gte) <= totalSelected
    );
    return qualifying[qualifying.length - 1] || null;
  }

  function getNextRechargeTier(activeTieredDiscount, totalSelected) {
    if (!activeTieredDiscount?.tiers?.length) return null;
    return (
      activeTieredDiscount.tiers.find((tier) => Number(tier.condition_quantity_gte) > totalSelected) || null
    );
  }

  function formatTierDiscountLabel(tier) {
    if (!tier) return '';
    if (tier.discount_type === 'percentage') {
      return `${Number(tier.discount_value)}% OFF`;
    }
    return `$${Number(tier.discount_value).toFixed(2)} OFF`;
  }

  function formatTierMilestoneLabel(tier) {
    if (!tier) return '';
    if (tier.discount_type === 'percentage') {
      return `Save ${Number(tier.discount_value)}%`;
    }
    return `Save $${Number(tier.discount_value).toFixed(2)}`;
  }

  class ClcBundleController {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.tierOverrides = sortTiers(config.tierDisplayOverrides || []);
      this.tiers = [];
      this.flavors = config.flavors || [];
      this.flavorMap = new Map(this.flavors.map((flavor) => [flavor.id, flavor]));
      this.items = [];
      this.displaySlotCount = Math.max(1, Number(config.slotCount) || 6);
      this.rechargeReady = false;
      this.rechargeBundleData = null;
      this.activeTieredDiscount = null;
      this.defaultSellingPlanId = normalizeSellingPlanId(config.sellingPlanId);
      this.useRecharge =
        config.pricingMode === 'recharge' && Boolean(config.rechargeEnabled && config.recharge?.bundleProductId);
    }

    async init() {
      this.root.classList.add('clc-bundle--initializing');

      if (!this.tiers.length) {
        this.tiers = this.buildDisplayTiers();
      }

      this.maxProgressCans = this.getMaxProgressCans();
      this.syncMilestoneDom();
      this.bindEvents();
      this.render();

      if (this.useRecharge) {
        try {
          await this.loadRecharge();
        } catch (error) {
          console.error('[clc-bundle] Recharge init failed, falling back to theme tiers.', error);
          this.useRecharge = false;
          this.root.dataset.clcBundleRechargeError = error.message || 'Recharge unavailable';
        }
      } else if (this.config.pricingMode === 'recharge') {
        const productFound = this.root.dataset.clcBundleRcProductFound === 'true';
        const collectionFound = this.root.dataset.clcBundleRcCollectionFound === 'true';
        let message = 'Recharge config incomplete. Set bundle product + collection in theme settings.';
        if (!productFound) {
          message = `Recharge bundle product not found (handle: ${this.root.dataset.clcBundleRcProduct || 'missing'}). Select the CLC Bundle product in Theme Editor.`;
        } else if (!collectionFound) {
          message = `Recharge bundle collection not found (handle: ${this.root.dataset.clcBundleRcCollection || 'missing'}).`;
        }
        this.root.dataset.clcBundleRechargeError = message;
        console.warn('[clc-bundle] Recharge disabled in config', {
          rechargeEnabled: this.config.rechargeEnabled,
          productFound,
          collectionFound,
          bundleProductId: this.config.recharge?.bundleProductId,
          collectionId: this.config.recharge?.collectionId,
          rcProduct: this.root.dataset.clcBundleRcProduct,
          rcCollection: this.root.dataset.clcBundleRcCollection,
        });
      }

      if (!this.tiers.length) {
        this.tiers = this.buildDisplayTiers();
      }

      this.syncMilestoneDom();
      this.render();
      this.root.classList.remove('clc-bundle--initializing');

      if (this.rechargeReady) {
        delete this.root.dataset.clcBundleRechargeError;
        this.root.dataset.clcBundleRecharge = 'true';
        console.info('[clc-bundle] Recharge connected', {
          bundleProductId: this.config.recharge.bundleProductId,
          bundleHandle: this.config.recharge.bundleHandle,
          tiers: this.tiers.map((tier) => ({
            minCans: tier.minCans,
            discountPercent: tier.discountPercent,
            label: tier.milestoneLabel,
          })),
        });
      }
    }

    resolveSellingPlanId(flavor) {
      return (
        normalizeSellingPlanId(flavor?.sellingPlanId) ??
        this.defaultSellingPlanId ??
        null
      );
    }

    resolveBundleVariantId() {
      if (this.config.recharge?.bundleVariantId) {
        return String(this.config.recharge.bundleVariantId);
      }

      const bundleProduct = this.rechargeBundleData;
      if (!bundleProduct?.variants?.length) return null;

      const defaultId = bundleProduct.default_bundle_variant_id;
      const matched =
        bundleProduct.variants.find(
          (variant) =>
            String(variant.id) === String(defaultId) ||
            String(variant.external_variant_id) === String(defaultId)
        ) || bundleProduct.variants[0];

      return matched?.external_variant_id ? String(matched.external_variant_id) : null;
    }

    async loadRecharge() {
      const recharge = await waitForRecharge();
      const getCdnBundleSettings = getCdnBundleSettingsFn(recharge);
      if (typeof getCdnBundleSettings !== 'function') {
        throw new Error('Recharge CDN bundle API unavailable.');
      }

      const bundleProductId = String(this.config.recharge.bundleProductId);
      const cdnApi = getRechargeCdnApi(recharge);
      this.rechargeBundleData = await getCdnBundleSettings.call(cdnApi, bundleProductId);

      const resolvedVariantId = this.resolveBundleVariantId();
      if (resolvedVariantId) {
        this.config.recharge.bundleVariantId = resolvedVariantId;
      } else if (!this.config.recharge.bundleVariantId) {
        throw new Error('Bundle variant ID missing. Activate the Recharge bundle and sync products.');
      }

      const tieredDiscounts = this.rechargeBundleData?.incentives?.tiered_discounts || [];
      const sellingPlanId = this.getPrimarySellingPlanId();

      this.activeTieredDiscount = getActiveTieredDiscount(
        tieredDiscounts,
        this.config.recharge.chargeType || 'checkout',
        sellingPlanId,
        Infinity
      );

      if (!this.activeTieredDiscount?.tiers?.length) {
        throw new Error('No published Recharge tiered discounts found for this bundle.');
      }

      this.rechargeReady = true;
      this.tiers = this.buildDisplayTiers();
    }

    buildDisplayTiers() {
      return sortTiers(this.tierOverrides).map((override) => ({
        minCans: Number(override.minCans) || 0,
        discountPercent: Number(override.discountPercent) || 0,
        milestoneLabel: override.milestoneLabel || '',
        badgeLabel: override.badgeLabel || '',
        rewardText: override.rewardText || '',
        source: this.rechargeReady ? 'recharge-display' : 'manual',
      }));
    }

    getMilestonePositions() {
      const count = this.tiers.length;
      if (count <= 0) return [];
      if (count === 1) return [50];
      // Milestones use equal flex columns; icon centers sit at (index + 0.5) / n.
      return Array.from({ length: count }, (_, index) => ((index + 0.5) / count) * 100);
    }

    getProgressFillPercent(count = this.getFilledCount()) {
      if (count <= 0 || !this.tiers.length) return 0;

      const positions = this.getMilestonePositions();

      for (let index = 0; index < this.tiers.length; index += 1) {
        const prevMin = index === 0 ? 0 : Number(this.tiers[index - 1].minCans);
        const currMin = Number(this.tiers[index].minCans);
        const prevPos = index === 0 ? 0 : positions[index - 1];
        const currPos = positions[index];

        if (count < currMin) {
          const span = Math.max(1, currMin - prevMin);
          const progress = Math.max(0, Math.min(1, (count - prevMin) / span));
          return prevPos + progress * (currPos - prevPos);
        }
      }

      return 100;
    }

    getProgressTierForCount(count = this.getFilledCount()) {
      let active = null;
      this.tiers.forEach((tier) => {
        if (count >= Number(tier.minCans)) active = tier;
      });
      return active;
    }

    getNextProgressTier(count = this.getFilledCount()) {
      return this.tiers.find((tier) => count < Number(tier.minCans)) || null;
    }

    getProgressBadgeLabel(count = this.getFilledCount()) {
      if (this.rechargeReady) {
        const rechargeTier = this.getCurrentRechargeTierForCount(count);
        if (rechargeTier) {
          const displayTier = this.getProgressTierForCount(count);
          if (displayTier?.badgeLabel) return displayTier.badgeLabel;
          return formatTierDiscountLabel(rechargeTier);
        }
      }

      return this.getProgressTierForCount(count)?.badgeLabel || '';
    }

    getPrimarySellingPlanId() {
      for (const flavor of this.flavorMap.values()) {
        const planId = this.resolveSellingPlanId(flavor);
        if (planId != null && planId !== ONETIME_SELLING_PLAN_ID) return planId;
      }
      return this.defaultSellingPlanId;
    }

    getMaxProgressCans() {
      const tierMax = this.tiers.reduce((max, tier) => Math.max(max, Number(tier.minCans) || 0), 0);
      return Math.max(tierMax, 1);
    }

    syncMilestoneDom() {
      this.root.querySelectorAll('[data-clc-bundle-progress]').forEach((progressRoot) => {
        const milestones = [...progressRoot.querySelectorAll('[data-clc-bundle-milestone]')];

        this.tiers.forEach((tier, index) => {
          let node = milestones[index];
          if (!node && milestones[0]?.parentElement) {
            node = milestones[0].cloneNode(true);
            milestones[0].parentElement.appendChild(node);
            milestones.push(node);
          }
          if (!node) return;

          node.hidden = false;
          node.dataset.minCans = String(tier.minCans);
          const label = node.querySelector('.clc-bundle__milestone-label');
          if (label) label.textContent = tier.milestoneLabel || '';
        });

        milestones.slice(this.tiers.length).forEach((node) => {
          node.hidden = true;
        });
      });
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    bindEvents() {
      if (this.eventsBound) return;
      this.eventsBound = true;

      this.root.addEventListener('click', (event) => {
        const addBtn = event.target.closest('[data-clc-bundle-flavor-add]');
        if (addBtn) {
          const card = addBtn.closest('[data-clc-bundle-flavor-card]');
          if (card?.dataset.flavorId) this.addFlavor(card.dataset.flavorId);
          return;
        }

        const increaseBtn = event.target.closest('[data-clc-bundle-flavor-increase]');
        if (increaseBtn) {
          const card = increaseBtn.closest('[data-clc-bundle-flavor-card]');
          if (card?.dataset.flavorId) this.addFlavor(card.dataset.flavorId);
          return;
        }

        const decreaseBtn = event.target.closest('[data-clc-bundle-flavor-decrease]');
        if (decreaseBtn) {
          const card = decreaseBtn.closest('[data-clc-bundle-flavor-card]');
          if (card?.dataset.flavorId) this.removeFlavor(card.dataset.flavorId);
          return;
        }

        const checkoutBtn = event.target.closest('[data-clc-bundle-checkout]');
        if (checkoutBtn) this.checkout();
      });
    }

    getFilledCount() {
      return this.items.length;
    }

    getMinItemsToCheckout() {
      const firstTierMin = this.tiers
        .map((tier) => Number(tier.minCans) || 0)
        .filter((min) => min > 0)
        .sort((a, b) => a - b)[0];

      if (firstTierMin) return firstTierMin;
      return Math.max(1, Number(this.config.minItemsToCheckout) || 1);
    }

    getFlavorCount(flavorId) {
      return this.items.filter((id) => id === flavorId).length;
    }

    getGroupedItems() {
      const groups = [];
      const indexById = new Map();

      this.items.forEach((flavorId) => {
        if (indexById.has(flavorId)) {
          groups[indexById.get(flavorId)].quantity += 1;
        } else {
          indexById.set(flavorId, groups.length);
          groups.push({ flavorId, quantity: 1 });
        }
      });

      return groups;
    }

    getMaxSlotCount() {
      const flavorCount = this.flavors.length;
      if (flavorCount <= 0) return this.displaySlotCount;
      return Math.min(this.displaySlotCount, flavorCount);
    }

    getActiveTier(count = this.getFilledCount()) {
      let active = null;
      this.tiers.forEach((tier) => {
        if (count >= Number(tier.minCans)) active = tier;
      });
      return active;
    }

    getNextTier(count = this.getFilledCount()) {
      return this.tiers.find((tier) => count < Number(tier.minCans)) || null;
    }

    getCurrentRechargeTierForCount(count = this.getFilledCount()) {
      if (!this.rechargeReady || !this.activeTieredDiscount) return null;
      return getCurrentRechargeTier(this.activeTieredDiscount, count);
    }

    getDiscountedPrice(flavor, count = this.getFilledCount()) {
      const compareBase = Number(flavor.comparePrice) || Number(flavor.unitPrice) || 0;
      let price = Number(flavor.subscriptionPrice ?? flavor.unitPrice) || 0;

      if (this.rechargeReady) {
        const currentTier = this.getCurrentRechargeTierForCount(count);
        if (currentTier) {
          price = calculateDiscount(price, currentTier.discount_value, currentTier.discount_type);
        }
        return price;
      }

      const percent = Number(this.getActiveTier(count)?.discountPercent) || 0;
      return Math.round(price * (1 - percent / 100));
    }

    getComparePrice(flavor) {
      return Number(flavor.comparePrice) || Number(flavor.unitPrice) || 0;
    }

    canAddFlavor(flavorId) {
      const flavor = this.flavorMap.get(flavorId);
      if (!flavor || !flavor.available) return false;
      const maxPerFlavor = Number(this.config.maxItemsPerFlavor) || 0;
      if (maxPerFlavor > 0 && this.getFlavorCount(flavorId) >= maxPerFlavor) return false;
      return true;
    }

    addFlavor(flavorId) {
      if (!this.canAddFlavor(flavorId)) return;
      this.items.push(flavorId);
      this.render();
    }

    removeFlavor(flavorId) {
      const index = this.items.lastIndexOf(flavorId);
      if (index === -1) return;
      this.items.splice(index, 1);
      this.render();
    }

    setHidden(node, hidden) {
      if (!node) return;
      node.hidden = hidden;
    }

    renderFlavorCards() {
      const count = this.getFilledCount();
      const activeTier = this.getActiveTier(count);
      const showCompare = Boolean(this.config.showCompareAtPrice);

      this.root.querySelectorAll('[data-clc-bundle-flavor-card]').forEach((card) => {
        const flavorId = card.dataset.flavorId;
        const flavor = this.flavorMap.get(flavorId);
        const qty = this.getFlavorCount(flavorId);
        const addBtn = card.querySelector('[data-clc-bundle-flavor-add]');
        const qtyWrap = card.querySelector('[data-clc-bundle-flavor-qty]');
        const qtyValue = card.querySelector('[data-clc-bundle-flavor-count]');
        const compareEl = card.querySelector('[data-clc-bundle-flavor-price-compare]');
        const currentEl = card.querySelector('[data-clc-bundle-flavor-price-current]');
        const increaseBtn = card.querySelector('[data-clc-bundle-flavor-increase]');
        const decreaseBtn = card.querySelector('[data-clc-bundle-flavor-decrease]');

        if (qty > 0) {
          card.classList.add('is-in-cart');
          this.setHidden(addBtn, true);
          this.setHidden(qtyWrap, false);
          if (qtyValue) qtyValue.textContent = String(qty);
        } else {
          card.classList.remove('is-in-cart');
          this.setHidden(addBtn, false);
          this.setHidden(qtyWrap, true);
        }

        if (flavor && currentEl) {
          const discounted = this.getDiscountedPrice(flavor, count);
          currentEl.textContent = `${formatMoney(discounted, this.config.moneyFormat)}${this.config.pricePerCanSuffix || ''}`;
          if (compareEl) {
            const comparePrice = this.getComparePrice(flavor);
            const showStrike = showCompare && comparePrice > discounted;
            compareEl.textContent = formatMoney(comparePrice, this.config.moneyFormat);
            this.setHidden(compareEl, !showStrike);
          }
        }

        if (addBtn) addBtn.disabled = !this.canAddFlavor(flavorId);
        if (increaseBtn) increaseBtn.disabled = !this.canAddFlavor(flavorId);
        if (decreaseBtn) decreaseBtn.disabled = qty <= 0;
      });
    }

    renderProgress() {
      const count = this.getFilledCount();
      const fillPercent = this.getProgressFillPercent(count);
      const activeTier = this.getProgressTierForCount(count);
      const nextTier = this.getNextProgressTier(count);

      this.root.querySelectorAll('[data-clc-bundle-progress-fill]').forEach((node) => {
        node.style.width = `${fillPercent}%`;
      });

      this.root.querySelectorAll('[data-clc-bundle-milestone]').forEach((node) => {
        if (node.hidden) return;
        const minCans = Number(node.dataset.minCans) || 0;
        node.classList.toggle('is-unlocked', count >= minCans);
        node.classList.toggle('is-active', activeTier && Number(activeTier.minCans) === minCans);
      });

      let message = '';
      let messageIsHtml = false;
      if (!nextTier) {
        const prefix = this.config.progressCompletePrefix || 'You unlocked the ';
        const highlight = this.config.progressCompleteHighlight || 'max savings';
        message = `${this.escapeHtml(prefix)}<span class="clc-bundle__progress-message-strong">${this.escapeHtml(highlight)}</span>`;
        messageIsHtml = true;
      } else {
        const remaining = Math.max(0, Number(nextTier.minCans) - count);
        const reward = nextTier.rewardText || nextTier.badgeLabel || nextTier.milestoneLabel || '';
        message =
          `Add <span class="clc-bundle__progress-message-strong">${remaining} more</span> to get ` +
          `<span class="clc-bundle__progress-message-strong">${this.escapeHtml(reward)}</span>`;
        messageIsHtml = true;
      }

      this.root.querySelectorAll('[data-clc-bundle-progress-message]').forEach((node) => {
        if (messageIsHtml) node.innerHTML = message;
        else node.textContent = message;
      });

      const badgeLabel = this.getProgressBadgeLabel(count);
      this.root.querySelectorAll('[data-clc-bundle-pack-badge]').forEach((node) => {
        if (badgeLabel) {
          node.textContent = badgeLabel;
          this.setHidden(node, false);
        } else {
          node.textContent = '';
          this.setHidden(node, true);
        }
      });
    }

    renderSlots() {
      const count = this.getFilledCount();

      this.root.querySelectorAll('[data-clc-bundle-slots]').forEach((group) => {
        const hideEmptySlots = group.dataset.clcBundleSlotsMode === 'mobile';
        group.innerHTML = '';

        const rows = [];
        const groupedItems = this.getGroupedItems();
        groupedItems.forEach(({ flavorId, quantity }) => {
          rows.push({ type: 'filled', flavorId, quantity });
        });

        if (!hideEmptySlots) {
          const maxSlots = this.getMaxSlotCount();
          const emptyCount = Math.max(0, maxSlots - groupedItems.length);
          for (let i = 0; i < emptyCount; i++) {
            rows.push({ type: 'empty' });
          }
        }

        this.setHidden(group, rows.length === 0);

        rows.forEach((rowData, index) => {
          const row = document.createElement('div');
          row.className = 'clc-bundle__slot-row';
          row.dataset.clcBundleSlotIndex = String(index);

          if (rowData.type === 'filled') {
            row.appendChild(
              this.buildFilledSlotRow(rowData.flavorId, rowData.quantity, count) || this.buildEmptySlotRow()
            );
          } else {
            row.appendChild(this.buildEmptySlotRow());
          }

          group.appendChild(row);

          if (index < rows.length - 1) {
            group.appendChild(this.createSlotDivider());
          }
        });
      });
    }

    createSlotDivider() {
      const divider = document.createElement('span');
      divider.className = 'clc-bundle__slot-divider';
      divider.setAttribute('aria-hidden', 'true');
      return divider;
    }

    buildEmptySlotRow() {
      const emptyText = this.config.emptySlotText || 'Empty slot';
      const slot = document.createElement('div');
      slot.className = 'clc-bundle__slot clc-bundle__slot--empty';
      slot.dataset.clcBundleSlotEmpty = 'true';

      const thumb = document.createElement('div');
      thumb.className = 'clc-bundle__slot-empty-thumb';
      thumb.setAttribute('aria-hidden', 'true');

      const label = document.createElement('p');
      label.className = 'clc-bundle__slot-empty-label';
      label.textContent = emptyText;

      slot.appendChild(thumb);
      slot.appendChild(label);
      return slot;
    }

    buildFilledSlotRow(flavorId, quantity = 1, count) {
      const showCompare = Boolean(this.config.showCompareAtPrice);
      const qtyPrefix = this.config.quantityPrefix || 'x';
      const flavor = this.flavorMap.get(flavorId);
      if (!flavor) return null;

      const unitCompare = this.getComparePrice(flavor);
      const unitCurrent = this.getDiscountedPrice(flavor, count);
      const qty = Math.max(1, Number(quantity) || 1);

      const slot = document.createElement('div');
      slot.className = 'clc-bundle__slot is-filled';

      const main = document.createElement('div');
      main.className = 'clc-bundle__slot-main';

      const leading = document.createElement('div');
      leading.className = 'clc-bundle__slot-leading';

      const thumb = document.createElement('div');
      thumb.className = 'clc-bundle__slot-thumb';

      if (flavor.image) {
        const image = document.createElement('img');
        image.className = 'clc-bundle__slot-image';
        image.src = flavor.image;
        image.alt = flavor.title;
        image.width = 50;
        image.height = 50;
        image.loading = 'lazy';
        thumb.appendChild(image);
      }

      leading.appendChild(thumb);

      const content = document.createElement('div');
      content.className = 'clc-bundle__slot-content';

      const title = document.createElement('p');
      title.className = 'clc-bundle__slot-title';
      title.textContent = flavor.title;

      const qtyEl = document.createElement('p');
      qtyEl.className = 'clc-bundle__slot-qty';
      qtyEl.textContent = `${qtyPrefix}${qty}`;

      content.appendChild(title);
      content.appendChild(qtyEl);

      main.appendChild(leading);
      main.appendChild(content);

      const prices = document.createElement('div');
      prices.className = 'clc-bundle__slot-prices';

      const comparePrice = document.createElement('span');
      comparePrice.className = 'clc-bundle__slot-price-compare';
      comparePrice.textContent = formatMoney(unitCompare, this.config.moneyFormat);
      if (!showCompare || unitCompare <= unitCurrent) comparePrice.hidden = true;

      const currentPrice = document.createElement('span');
      currentPrice.className = 'clc-bundle__slot-price-current';
      currentPrice.textContent = formatMoney(unitCurrent, this.config.moneyFormat);

      prices.appendChild(comparePrice);
      prices.appendChild(currentPrice);

      slot.appendChild(main);
      slot.appendChild(prices);
      return slot;
    }

    renderTotals() {
      const count = this.getFilledCount();
      let compareTotal = 0;
      let currentTotal = 0;

      this.items.forEach((flavorId) => {
        if (!flavorId) return;
        const flavor = this.flavorMap.get(flavorId);
        if (!flavor) return;
        compareTotal += this.getComparePrice(flavor);
        currentTotal += this.getDiscountedPrice(flavor, count);
      });

      const savings = Math.max(0, compareTotal - currentTotal);
      const savingsAmount = formatMoney(savings, this.config.moneyFormat);
      const savingsPrefix = (this.config.savingsLabelPrefix || 'You save ').replace(/\[amount\]/g, '').trim();
      const savingsHtml = `${this.escapeHtml(savingsPrefix)}<span class="clc-bundle__total-savings-strong">${this.escapeHtml(savingsAmount)} </span>`;

      const canCheckout = count >= this.getMinItemsToCheckout();

      this.root.querySelectorAll('[data-clc-bundle-total]').forEach((node) => {
        this.setHidden(node, count === 0);
      });

      this.root.querySelectorAll('[data-clc-bundle-total-compare]').forEach((node) => {
        const showCompare = Boolean(this.config.showCompareAtPrice) && savings > 0;
        node.textContent = formatMoney(compareTotal, this.config.moneyFormat);
        this.setHidden(node, !showCompare);
      });

      this.root.querySelectorAll('[data-clc-bundle-total-current]').forEach((node) => {
        node.textContent = formatMoney(currentTotal, this.config.moneyFormat);
      });

      this.root.querySelectorAll('[data-clc-bundle-total-savings]').forEach((node) => {
        if (savings > 0) {
          node.innerHTML = savingsHtml;
          this.setHidden(node, false);
        } else {
          node.textContent = '';
          this.setHidden(node, true);
        }
      });

      this.root.querySelectorAll('[data-clc-bundle-checkout]').forEach((button) => {
        button.disabled = !canCheckout;
      });
    }

    render() {
      try {
        this.renderFlavorCards();
        this.renderProgress();
        this.renderSlots();
        this.renderTotals();
      } catch (error) {
        console.error('[clc-bundle] Render failed', error);
      }
    }

    buildManualCartItems() {
      const counts = new Map();
      this.items.forEach((flavorId) => {
        if (!flavorId) return;
        const flavor = this.flavorMap.get(flavorId);
        if (!flavor) return;
        counts.set(flavor.variantId, (counts.get(flavor.variantId) || 0) + 1);
      });

      const propertyKey = this.config.bundlePropertyKey || '_bundle_id';
      const propertyValue = this.config.bundlePropertyValue || 'flow-pack';

      return [...counts.entries()].map(([variantId, quantity]) => {
        const flavor = [...this.flavorMap.values()].find((row) => String(row.variantId) === String(variantId));
        const item = {
          id: Number(variantId),
          quantity,
          properties: {
            [propertyKey]: propertyValue,
          },
        };
        const planId = this.resolveSellingPlanId(flavor);
        if (planId != null) item.selling_plan = planId;
        return item;
      });
    }

    buildRechargeSelections() {
      const counts = new Map();
      this.items.forEach((flavorId) => {
        if (!flavorId) return;
        const flavor = this.flavorMap.get(flavorId);
        if (!flavor) return;
        const key = String(flavor.variantId);
        counts.set(key, {
          flavor,
          quantity: (counts.get(key)?.quantity || 0) + 1,
        });
      });

      return [...counts.values()].map(({ flavor, quantity }) => {
        const sellingPlan = this.resolveSellingPlanId(flavor);
        const selection = {
          collectionId: String(this.config.recharge.collectionId),
          externalProductId: String(flavor.productId),
          externalVariantId: String(flavor.variantId),
          quantity,
        };
        if (sellingPlan != null) selection.sellingPlan = sellingPlan;
        return selection;
      });
    }

    async buildRechargeCartItems() {
      const bundleApi = getRechargeBundleEngine(window.recharge);
      if (!bundleApi?.getDynamicBundleItems) {
        throw new Error('Recharge bundle checkout API unavailable.');
      }

      const selections = this.buildRechargeSelections();
      if (!selections.length) return [];

      const bundle = {
        externalProductId: String(this.config.recharge.bundleProductId),
        externalVariantId: String(this.config.recharge.bundleVariantId),
        selections,
      };

      if (typeof bundleApi.validateDynamicBundle === 'function') {
        const validation = await bundleApi.validateDynamicBundle(bundle);
        if (validation !== true) {
          throw new Error(typeof validation === 'string' ? validation : 'Invalid bundle selection.');
        }
      }

      return bundleApi.getDynamicBundleItems(bundle, this.config.recharge.bundleHandle);
    }

    async checkout() {
      if (this.getFilledCount() < this.getMinItemsToCheckout()) return;

      this.root.classList.add('is-loading');

      try {
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });

        let items = [];
        if (this.useRecharge && this.rechargeReady) {
          items = await this.buildRechargeCartItems();
        } else {
          items = this.buildManualCartItems();
        }

        if (!items.length) {
          throw new Error('No items to add.');
        }

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Unable to add bundle to cart.');
        }

        const redirect = this.config.redirectAfterAdd || 'checkout';
        if (redirect === 'none') return;

        const destination = redirect === 'cart' ? '/cart' : '/checkout';
        const root = window.Shopify?.routes?.root || '/';

        if (!this.useRecharge) {
          const activeTier = this.getActiveTier();
          const discountCode = String(activeTier?.discountCode || '').trim();
          if (discountCode) {
            window.location.href = `${root}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(destination)}`;
            return;
          }
        }

        window.location.href = `${root}${destination.replace(/^\//, '')}`;
      } catch (error) {
        console.error('[clc-bundle]', error);
        window.alert(error.message || 'Something went wrong. Please try again.');
      } finally {
        this.root.classList.remove('is-loading');
      }
    }
  }

  async function initBundle(root) {
    if (root.dataset.clcBundleInit === 'true') return;
    const config = parseConfig(root);
    if (!config) return;
    root.dataset.clcBundleInit = 'true';
    const controller = new ClcBundleController(root, config);
    await controller.init();
  }

  async function initAll() {
    const roots = [...document.querySelectorAll('clc-bundle.clc-bundle')];
    await Promise.all(roots.map((root) => initBundle(root)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAll().catch((error) => console.error('[clc-bundle]', error));
    });
  } else {
    initAll().catch((error) => console.error('[clc-bundle]', error));
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('clc-bundle.clc-bundle');
    if (root) {
      initBundle(root).catch((error) => console.error('[clc-bundle]', error));
    }
  });

  class ClcCheckoutTimer extends HTMLElement {
    connectedCallback() {
      const seconds = Number(this.dataset.durationSeconds);
      this.valueEl = this.querySelector('[data-timer-value]');
      this.endTime = Date.now() + Math.max(0, seconds) * 1000;
      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) window.clearInterval(this.interval);
    }

    tick() {
      if (!this.valueEl) return;
      const remainingMs = Math.max(0, this.endTime - Date.now());
      const totalSeconds = Math.floor(remainingMs / 1000);
      const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
      const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      this.valueEl.textContent = `${hours}:${minutes}:${seconds}`;
    }
  }

  if (!customElements.get('clc-checkout-timer')) {
    customElements.define('clc-checkout-timer', ClcCheckoutTimer);
  }
})();
